/**
 * The Unassigned register (TMMAPP-0108, the tmm-api review amendment; `GET
 * /v1/virtual-accounts/{id}/unassigned/register`): the virtual account's Unassigned parts as
 * register rows — date, description with its source account, category, the signed amount and the
 * served running balance — opened from the Unassigned figure's menu. Newest first, cursor paging
 * ("Load older lines"); a read-only dialog closes with a contained Close.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { categoriesApi, type Category } from '@/api/categories';
import { registersApi, type EnvelopeRegisterLine, type Page } from '@/api/registers';
import type { VirtualAccountDetail } from '@/api/virtualAccounts';
import { DialogSubtitle } from '@/components/DialogSubtitle';
import { ErrorState } from '@/components/ErrorState';
import { MoneyText } from '@/components/MoneyText';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { resolveTokens } from '@/theme/theme';

interface UnassignedRegisterDialogProps {
  virtualAccount: VirtualAccountDetail;
  onClose: () => void;
}

export function UnassignedRegisterDialog({
  virtualAccount,
  onClose,
}: UnassignedRegisterDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const fullScreen = useDialogFullScreen();
  const titleId = useId();
  const subtitleId = useId();
  const [page, setPage] = useState<Page<EnvelopeRegisterLine> | null>(null);
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const [busy, setBusy] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);

  const load = useCallback(async () => {
    try {
      setPage(await registersApi.unassignedRegister(virtualAccount.id));
      setFailure(null);
    } catch (error) {
      setFailure({ error });
    }
  }, [virtualAccount.id]);

  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
    void categoriesApi
      .list(virtualAccount.id)
      .then(setCategories)
      .catch(() => {});
  }, [load, virtualAccount.id]);

  async function more() {
    if (page?.nextCursor == null || busy) return;
    setBusy(true);
    try {
      const next = await registersApi.unassignedRegister(virtualAccount.id, {
        cursor: page.nextCursor,
      });
      setPage({ items: [...page.items, ...next.items], nextCursor: next.nextCursor });
    } catch (error) {
      setFailure({ error });
    } finally {
      setBusy(false);
    }
  }

  const categoryName = (id: number | null) =>
    id === null
      ? t('envelope.uncategorized')
      : (categories.find((category) => category.id === id)?.name ?? '—');

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby={titleId}
      aria-describedby={subtitleId}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="md"
    >
      <DialogTitle id={titleId}>{t('virtualAccount.unassignedRegister.title')}</DialogTitle>
      <DialogSubtitle id={subtitleId}>
        {t('virtualAccount.unassignedRegister.sub', { name: virtualAccount.name })}
      </DialogSubtitle>
      <DialogContent>
        {failure !== null && (
          <ErrorState
            variant="section"
            error={failure.error}
            what={t('errors.what.virtualAccountRegister')}
            onRetry={() => void load()}
          />
        )}
        {page === null && failure === null && (
          <Skeleton variant="rounded" height={120} sx={{ mt: 2 }} />
        )}
        {page !== null && page.items.length === 0 && (
          <Typography sx={{ mt: 2, fontSize: 13, color: tokens.text2 }}>
            {t('virtualAccount.unassignedRegister.empty')}
          </Typography>
        )}
        {page !== null && page.items.length > 0 && (
          <TableContainer sx={{ mt: 2 }}>
            <Table size="small" aria-labelledby={titleId} sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('envelope.colDate')}</TableCell>
                  <TableCell>{t('virtualAccount.ledger.colDescription')}</TableCell>
                  <TableCell>{t('envelope.colCategory')}</TableCell>
                  <TableCell align="right">{t('envelope.colAmount')}</TableCell>
                  <TableCell align="right">{t('envelope.colBalance')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {page.items.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell sx={{ color: tokens.text2, whiteSpace: 'nowrap' }}>
                      {formatCivilDate(line.postedOn, locale, { month: 'short', day: 'numeric' })}
                    </TableCell>
                    <TableCell>
                      <Box>{line.payee}</Box>
                      {line.account !== null && (
                        <Typography sx={{ fontSize: 11, color: tokens.textLabel('surfaceRaised') }}>
                          {line.account.last4 === null
                            ? line.account.name
                            : t('virtualAccount.ledger.source', {
                                account: line.account.name,
                                last4: line.account.last4,
                              })}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{categoryName(line.categoryId)}</TableCell>
                    <TableCell align="right">
                      <MoneyText
                        minor={line.amountMinor}
                        currency={virtualAccount.currency}
                        tone="register"
                        variant="body2"
                        signed
                      />
                    </TableCell>
                    <TableCell align="right">
                      <MoneyText
                        minor={line.runningBalanceMinor}
                        currency={virtualAccount.currency}
                        variant="body2"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {page?.nextCursor && (
          <Stack direction="row" sx={{ justifyContent: 'center', mt: 2 }}>
            <Button variant="outlined" loading={busy} onClick={() => void more()}>
              {t('common.loadOlderLines')}
            </Button>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
